<template>
    <div class="relative" :class="rootClass" :style="rootStyle" v-bind="nonEventAttrs">
        <input
            class="form-control"
            :class="classes"
            :disabled="disabled"
            :placeholder="placeholder"
            :type="type"
            :value="resolvedValue"
            :readonly="readonly"
            :maxlength="maxlength"
            ref="input"
            v-on="inputListeners"
        />
        <div
            class="absolute h-full flex items-center justify-center left-0 top-0 w-12 pointer-events-none"
            v-if="$slots.icon"
        >
            <slot name="icon" />
        </div>
        <div
            class="absolute h-full flex items-center justify-center right-0 top-0 w-12"
            v-if="error"
        >
            <activix-tooltip :content="error">
                <icon class="text-xl text-red-500" name="regular/alert-circle" />
            </activix-tooltip>
        </div>
    </div>
</template>

<script>
    import { splitAttrs } from '@/utils/vue-related.ts';

    export default {
        name: 'ActivixInput',

        // FIXME(VUE3): Remove once compat mode is removed; real Vue 3 merges listeners into $attrs by default.
        compatConfig: { MODE: 3 },

        inheritAttrs: false,

        emits: ['input', 'update:modelValue'],

        props: {
            inputClass: {
                type: [String, Array],
                default: '',
            },
            type: {
                type: String,
                default: 'text',
            },
            value: {
                type: String,
                default: '',
            },
            modelValue: {
                type: String,
                default: undefined,
            },
            placeholder: {
                type: String,
                default: '',
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            readonly: {
                type: Boolean,
                default: false,
            },
            maxlength: {
                type: Number,
                default: 255,
            },
            error: {
                type: String,
                default: '',
            },
            hasError: {
                type: Boolean,
                default: false,
            },
            white: {
                type: Boolean,
                default: false,
            },
        },

        computed: {
            resolvedValue() {
                return this.modelValue !== undefined ? this.modelValue : this.value;
            },

            classes() {
                const inputClass = Array.isArray(this.inputClass)
                    ? this.inputClass
                    : [this.inputClass];

                return inputClass.concat([
                    {
                        'has-error': this.hasError || !!this.error,
                        'pl-12': !!this.$slots.icon,
                        'input-white': this.white,
                    },
                ]);
            },

            nonEventAttrs() {
                // FIXME(VUE3): Remove splitAttrs after full Vue 3 migration
                const nonEventAttrs = { ...splitAttrs(this.$attrs).nonEventAttrs };

                // Vue 2 magically applied class/style to the root element even with
                // inheritAttrs: false. Vue 3 doesn't — they're just regular $attrs now.
                // Since this component routes attrs to different children (class/style
                // to the wrapper <div>, events to the <input>), we strip them here and
                // bind them manually via :class="rootClass" / :style="rootStyle".
                delete nonEventAttrs.class;
                delete nonEventAttrs.style;

                return nonEventAttrs;
            },

            rootClass() {
                return this.$attrs.class;
            },

            rootStyle() {
                return this.$attrs.style;
            },

            inputListeners() {
                return {
                    ...splitAttrs(this.$attrs).listeners,
                    input: (event) => {
                        const newValue = event.target.value;
                        this.$emit('input', newValue);
                        this.$emit('update:modelValue', newValue);
                    },
                };
            },
        },

        methods: {
            focus() {
                this.$refs.input.focus();
            },
        },
    };
</script>
